// EDEN · "Books for this lesson" — one clear pick per row for every week of the year plan:
// Coursebook · Workbook · Grammar (Oxford Grammar for Schools) · Reading · Test.
//
// Owner, 2026-10-01: "Teachers just want to know related references which they can use for their
// lessons. If they click a lesson and can't decide which materials to use, our project fails."
// Before this card the page numbers sat inside a fold of 12–87 mixed items, workbook pages were
// never shown, and Grammar for Schools appeared as "Unit 17" with no topic.
//
// The data is GENERATED by scripts/build-lesson-refs.mjs (prebuild) from the curriculum, the
// citation graph and the Grammar for Schools contents pages — nothing here is typed per week. A
// row with nothing to show says so in plain words ("No workbook in this course"), so a teacher
// learns that the empty row is a fact, not a fault. Five rows, same order, on every lesson.

import { esc } from "./dom";
import { icon } from "./icons";
import { plainText, taskLabel } from "./lesson-data";
import { expandPages, openButtonHtml, registerTarget, type PageSeg, type ReaderTarget, type GuideTarget, type Track } from "./book-reader";
import { extrasFor, fileHref, loadLessonExtras, practiceTarget, type LessonExtras } from "./library";
import type { CurriculumNode } from "./curriculum";

type Part = [label: string, pages: string | null];
type None = { none: string; text?: string | null; grade?: number; book?: string; books?: string[]; book_id?: string; book_ids?: string[] };

/** BOOK-READER-1: the shelf id of the book a row names, and — only where the page map is trusted and the
 *  book is on curriculum/books-online.json — its PDF pages as [firstPdf, lastPdf, printed − pdf] runs.
 *  `unit_pdf`: the whole unit, when it is more than the row's pages. No `pdf` → the row keeps its text, no button. */
export interface ReaderFields { book_id?: string; pdf?: PageSeg[]; unit_pdf?: PageSeg[]; missing_printed?: number[]; answers?: GuideTarget; teacherOnly?: boolean }

/** `note`: a week that finishes a unit with no new spread ("Finish the unit — no new pages this week.") */
export interface RefCoursebook extends ReaderFields { book: string; unit: string | null; parts: Part[]; note?: string }
export interface RefWorkbook extends ReaderFields { book: string; parts: Part[] }
/** `hw`: the year plan itself sets these pages (as homework) — the pages are the plan's, not the whole unit.
 *  `after`: the revision pages the book prints straight after the unit (F-2026-10-07-06) — never the unit's. */
export interface RefGrammar extends ReaderFields { book: string; unit: number; title: string; pages: string; hw?: 1; after?: RefGrammarAfter }
export interface RefGrammarAfter extends ReaderFields { title: string; pages: string }
export interface RefReading extends ReaderFields { book: string; part: string; pages?: string | null; why?: string; also?: string[] }
export type RefTest =
  | { kind: "school"; event: string; taught?: string[] }
  | ({ kind: "unit-test"; book: string; unit: string; page_count: number | null } & ReaderFields)
  | ({ kind: "cambridge"; task: string | null; book: string | null; section: string | null; pages: string | null; book_ids?: string[]; test?: string; notice?: string; answer_note?: string; companions?: (GuideTarget & { teacherOnly?: boolean })[] } & ReaderFields);

/** One line of the optional "More practice" list under the card (built precision-first). */
export interface RefExtra extends ReaderFields { book: string; part: string | null; pages: string | null; why: string }

/** The reader button for a row, or "" when the row has no trusted pages. */
export function readerButton(f: ReaderFields, book: string | null | undefined, label: string, unitLabel?: string | null, cls = "", more: Partial<ReaderTarget> = {}): string {
  if (!f.book_id || !book) return "";
  const missing = (f.missing_printed ?? []).map((printed) => openButtonHtml({ book, bookId: f.book_id!, label: `Student Book page ${printed} · not in this PDF`, pages: [], missing: printed }, cls)).join("");
  if (!f.pdf?.length) return missing;
  const t: ReaderTarget = { book, bookId: f.book_id, label, pages: f.pdf, teacherOnly: f.teacherOnly, ...(f.answers ? { guide: f.answers } : {}), ...more };
  if (f.unit_pdf?.length) t.unit = { label: unitLabel || label, pages: f.unit_pdf };
  return openButtonHtml(t, cls) + missing;
}
function answerButton(f: ReaderFields, prefix = ""): string {
  const a = f.answers;
  if (!a?.pdf.length) return "";
  const label = prefix ? `${prefix} · ${a.label}` : a.label;
  return `<span class="bk-answer-label">${esc(label)}</span>${openButtonHtml({ book: a.book, bookId: a.book_id, label, pages: a.pdf, teacherOnly: true }, "bk-answers")}`;
}
function companionButtons(companions: (GuideTarget & { teacherOnly?: boolean })[] = []): string {
  return companions.map((c) => `<span class="bk-answer-label">${esc(c.label)}</span>${openButtonHtml({ book: c.book, bookId: c.book_id, label: c.label, pages: c.pdf, teacherOnly: c.teacherOnly ?? true }, "bk-companion")}`).join("");
}

export interface LessonRef {
  /** a teacher-facing title for code-like week titles ("U1 L2-L3" → "Unit 1: … · Lessons 2–3") */
  t?: string;
  /** more practice beyond the five rows — shown in the lesson page's fold, never on the card */
  x?: RefExtra[];
  cb: RefCoursebook | None;
  wb: RefWorkbook | None;
  gr: RefGrammar | None;
  rd: RefReading[] | None;
  ts: RefTest[] | None;
}

interface LessonRefsFile {
  schema: number;
  nodes: Record<string, LessonRef>;
}

let cache: Promise<LessonRefsFile | null> | null = null;
let loaded: LessonRefsFile | null = null;
/** Which week a loaded row belongs to, so the card can add that week's library extras without new arguments. */
const idOf = new WeakMap<LessonRef, string>();

/** The whole file, loaded once (code-split by Vite, ~210 KB raw / ~17 KB gzip). The JSON is generated
 *  AND committed (like materials-index.json) — the import needs it at build time. A failed load (an old
 *  chunk after a redeploy, a dropped connection) is not remembered: the next screen tries again, and
 *  the cards simply stay away until it works. */
export function loadLessonRefs(): Promise<LessonRefsFile | null> {
  if (!cache) {
    // T1 review: the week's extras (answers, this fortnight's story, test audio, practice sheets) load alongside;
    // a failed extras load only leaves those lines out.
    cache = import("../curriculum/lesson-refs.json")
      .then((m) => {
        loaded = (m.default ?? m) as unknown as LessonRefsFile;
        for (const [id, r] of Object.entries(loaded.nodes)) idOf.set(r, id);
        return loaded;
      })
      .catch(() => {
        cache = null;
        return null;
      });
  }
  // loadLessonExtras() keeps a loaded file and retries a failed one, so every screen asks again until it arrives
  const refs = cache;
  return Promise.all([refs, loadLessonExtras()]).then(([r]) => r);
}

/** One week's books, or null when the file is missing (a stale build) or the week is unknown. */
export async function refsFor(nodeId: string | null | undefined): Promise<LessonRef | null> {
  if (!nodeId) return null;
  const f = await loadLessonRefs();
  return f?.nodes[nodeId] ?? null;
}

const isNone = (x: unknown): x is None => !!x && typeof x === "object" && "none" in (x as object);

/** The light expansions every week title gets (no book data needed): U1 → Unit 1, BP4 → Blueprint 4. */
export function lightTitle(s: string): string {
  return s
    .replace(/^U(\d+)\s+(?=[A-Za-z])/, "Unit $1 · ")
    .replace(/^M(\d+)\s+(?=[A-Za-z])/, "Module $1 · ")
    .replace(/\bU(\d+)\s*-\s*U(\d+)\b/g, "Units $1–$2")
    .replace(/\bM(\d+)\s*-\s*M(\d+)\b/g, "Modules $1–$2")
    .replace(/\bU(\d+)\b/g, "Unit $1")
    .replace(/\bM(\d+)\b/g, "Module $1")
    .replace(/\bL(\d{1,2})\s*-\s*L?(\d{1,2})\b/g, "Lessons $1–$2")
    .replace(/\bBP(\d)\b/g, "Blueprint $1")
    .replace(/^LB(\d)\s+/, "Let's Begin $1 · ")
    .replace(/\bLB(\d)\b/g, "Let's Begin $1")
    .replace(/\bL(\d{1,2})\b/g, "Lesson $1");
}

/** The title a teacher reads for a week: the generated one when the books file is loaded (it knows
 *  the unit's name — a screen that lists weeks awaits loadLessonRefs() first), else the light
 *  expansion. Always through plainText (no internal codes). */
export function teacherTitle(node: Pick<CurriculumNode, "title"> & { id?: string }, ref?: LessonRef | null): string {
  const t = ref?.t ?? (node.id ? loaded?.nodes[node.id]?.t : undefined);
  return plainText(t ?? lightTitle(node.title));
}

// ── wording ────────────────────────────────────────────────────────────────────────────────────

function pagesWord(p: string | null | undefined): string {
  if (!p) return "";
  return /[–,-]/.test(p) ? `pages ${p}` : `page ${p}`;
}

function partsHtml(parts: Part[]): string {
  return parts
    .map(([label, pages]) => `<span class="bk-part">${label ? `${esc(label)}${pages ? " — " : ""}` : ""}${esc(pagesWord(pages))}</span>`)
    .join(`<span class="bk-sep" aria-hidden="true"> · </span>`);
}

const NONE_TEXT: Record<string, Record<string, string>> = {
  cb: {
    "no-new-pages": "No new coursebook pages this week.",
    "plan-text": "",
  },
  wb: {
    "no-workbook-in-course": "This course has no workbook — this week's Worksheet is the practice.",
    "no-workbook": "This course has no workbook.",
    "no-pairing": "No workbook pages are paired with this week.",
    "no-new-pages": "No new workbook pages this week.",
  },
  gr: {
    review: "No new grammar this week.",
    "no-match": "No Grammar for Schools unit matches this week's grammar — use the grammar box in the coursebook.",
  },
  rd: {
    "no-match": "No reading book is linked to this week.",
    "no-book-for-grade": "No reading book is set for this grade yet.",
  },
  // This row is the exam-book tests; a school test (monthly, SA) is named by the week's "At school" line, on the calendar's week
  // — so the card must not say "No test this week." beside it (owner ruling A2, 21 Oct 2026).
  ts: { "no-test": "No exam-book test this week." },
};

function noneHtml(row: keyof typeof NONE_TEXT, v: None): string {
  if (row === "gr" && v.none === "no-book-for-grade") {
    return `<span class="bk-none">No Grammar for Schools book is set for Grade ${esc(String(v.grade ?? ""))}.</span>`;
  }
  if (row === "gr" && v.none === "no-match" && v.book) {
    return `<span class="bk-none">No unit of ${esc(v.book)} matches this week's grammar — use the grammar box in the coursebook.</span>`;
  }
  if (row === "rd" && v.none === "no-match" && v.books?.length) {
    return `<span class="bk-none">No reading unit matches this week's words. This grade's reading books: ${esc(v.books.join(", "))}.</span>`;
  }
  if (row === "cb" && v.text) {
    return v.none === "plan-text" ? `<span class="bk-main">${esc(plainText(v.text))}</span>` : `<span class="bk-none">${esc(v.text)}</span>`;
  }
  const text = NONE_TEXT[row]?.[v.none] ?? "";
  return text ? `<span class="bk-none">${esc(text)}</span>` : "";
}

/** An answer key one tap away, as its own labelled button (teacher-only: never on the Present screen). */
function guideButton(g: GuideTarget | undefined, prefix: string): string {
  if (!g?.pdf.length) return "";
  const label = `${prefix} · ${g.label}`;
  return `<span class="bk-answer-label">${esc(label)}</span>${openButtonHtml({ book: g.book, bookId: g.book_id, label, pages: g.pdf, teacherOnly: true }, "bk-answers")}`;
}
/** "Audio inside: 31 tracks" — teachers told us they could not see that the audio exists. */
function audioHint(tracks: Track[] | undefined): string {
  if (!tracks?.length) return "";
  const words = tracks.length === 1 ? tracks[0]!.label : `${tracks.length} tracks`;
  return `<span class="bk-audio">${icon("audio", "ico-sm")}<span>With audio: ${esc(words)}</span></span>`;
}
/** One listening track to play right here on the card (the gate checks the sign-in). */
function listenHtml(bookId: string, t: Track): string {
  return `<span class="bk-listen">${icon("audio", "ico-sm")}<span>${esc(t.label)}</span><audio controls preload="none" src="/m/${encodeURIComponent(bookId)}/audio/${encodeURIComponent(t.id)}"></audio></span>`;
}
function cbHtml(v: LessonRef["cb"], ex: LessonExtras | null): string {
  if (isNone(v)) return noneHtml("cb", v);
  const pages = v.parts.length ? partsHtml(v.parts) : esc(v.note ?? "");
  const what = v.unit ?? (v.parts.map((p) => p[0]).filter(Boolean).join(", ") || "This week's pages");
  const more: Partial<ReaderTarget> = {};
  if (ex?.cb_tracks?.length) more.tracks = ex.cb_tracks;
  if (ex?.cb_answers && !v.answers) more.guide = ex.cb_answers;
  return `<span class="bk-main"><b>${esc(v.book)}</b>${v.unit ? ` · ${esc(v.unit)}` : ""}</span>${pages ? `<span class="bk-pages">${pages}</span>` : ""}${
    readerButton(v, v.book, what, v.unit ? `${v.unit} · the whole unit` : null, "", more)}${v.pdf?.length ? audioHint(ex?.cb_tracks) : ""}${guideButton(ex?.cb_answers, "Answers")}`;
}
function wbHtml(v: LessonRef["wb"], ex: LessonExtras | null): string {
  if (isNone(v)) return noneHtml("wb", v);
  const more: Partial<ReaderTarget> = ex?.wb_answers && !v.answers ? { guide: ex.wb_answers } : {};
  return `<span class="bk-main"><b>${esc(v.book)}</b></span><span class="bk-pages">${partsHtml(v.parts)}</span>${readerButton(v, v.book, "This week's workbook pages", null, "", more)}${guideButton(ex?.wb_answers, "Answers")}`;
}
/** The week's practice worksheets, one tappable line each: the reader shows the pages, plays the audio and opens
 *  the answer key; the Word file (and its key) download beside it. Grouped by bundle, in the index's order. */
function prHtml(ex: LessonExtras | null): string {
  const list = ex?.practice ?? [];
  const books = [...new Set(list.map((p) => p.book_id))];
  return books.map((bid) => {
    const mine = list.filter((p) => p.book_id === bid);
    const items = mine.map((p) => {
      const n = expandPages(p.pdf).length;
      const meta = [`${n} ${n === 1 ? "page" : "pages"}`, p.tracks?.length ? `${p.tracks.length} ${p.tracks.length === 1 ? "track" : "tracks"}` : "",
        p.key?.length ? "answer key" : p.key_none ? "no key" : ""].filter(Boolean).join(" · ");
      const files = [
        p.file ? `<a class="bk-file" href="${esc(fileHref(p.book_id, p.file))}" download>${icon("download", "ico-sm")}<span>Word</span></a>` : "",
        p.key_file ? `<a class="bk-file" href="${esc(fileHref(p.book_id, p.key_file))}" download>${icon("download", "ico-sm")}<span>Key (Word)</span></a>` : "",
      ].join("");
      return `<li><button type="button" class="bk-sheet" data-book-open="${registerTarget(practiceTarget(p))}">${icon("sheet", "ico-sm")}<span class="bk-sheet-name">${esc(p.label)}</span><span class="bk-sheet-meta">${esc(meta)}</span></button>${files ? `<span class="bk-files">${files}</span>` : ""}</li>`;
    }).join("");
    return `<span class="bk-main"><b>${esc(mine[0]!.book)}</b> · ${mine.length === 1 ? "one sheet" : `${mine.length} sheets`} for this week</span><ul class="bk-sheets">${items}</ul>`;
  }).join("");
}
function grHtml(v: LessonRef["gr"]): string {
  if (isNone(v)) return noneHtml("gr", v);
  const unit = `Unit ${v.unit}: ${v.title}`;
  const after = v.after
    ? `<span class="bk-after"><span class="bk-main">${esc(v.after.title)}</span><span class="bk-pages">${esc(pagesWord(v.after.pages))} — revision straight after the unit</span>${
      readerButton(v.after, v.book, v.after.title)}</span>`
    : "";
  return `<span class="bk-main"><b>${esc(v.book)}</b> · ${esc(unit)}</span><span class="bk-pages">${esc(pagesWord(v.pages))}${v.hw ? " — set as homework in the year plan" : ""}</span>${
    readerButton(v, v.book, unit, `${unit} · the whole unit`)}${answerButton(v, `Answers to Unit ${v.unit}`)}${after}`;
}
/** This fortnight's story or skills unit (scripts/build-library.mjs READING_PLAN), with its audio and answers. */
function picksHtml(ex: LessonExtras | null): string {
  return (ex?.reading ?? []).map((r) => {
    const t: ReaderTarget = { book: r.book, bookId: r.book_id, label: r.label, pages: r.pdf };
    if (r.tracks?.length) t.tracks = r.tracks;
    if (r.answers) t.guide = r.answers;
    return `<span class="bk-main"><b>${esc(r.book)}</b> · ${esc(r.label)}</span>${r.sub ? `<span class="bk-pages">${esc(r.sub)}</span>` : ""}${
      openButtonHtml(t)}${audioHint(r.tracks)}${r.no_audio ? `<span class="bk-pages">${esc(r.no_audio)}</span>` : ""}${guideButton(r.answers, "Answers")}`;
  }).join("");
}
function rdHtml(v: LessonRef["rd"], ex: LessonExtras | null): string {
  const picks = picksHtml(ex);
  if (isNone(v)) return picks || noneHtml("rd", v);
  return picks + v
    .map((r) => `<span class="bk-main"><b>${esc(r.book)}</b> · ${esc(r.part)}</span>${
      r.pages || r.why ? `<span class="bk-pages">${esc([pagesWord(r.pages), r.why ? `uses ${r.why}` : ""].filter(Boolean).join(" · "))}</span>` : ""
    }${readerButton(r, r.book, r.part)}`)
    .join("");
}
/** The graph's section ("rw Part 1", "listening Part 2") in a teacher's words: its skill word is spelled out. */
const SECTION_WORD: Record<string, string> = {
  rw: "Reading & Writing", listening: "Listening", reading: "Reading", writing: "Writing", speaking: "Speaking",
};
const sectionLabel = (s: string): string => s.replace(/^\w+/, (w) => SECTION_WORD[w.toLowerCase()] ?? w.charAt(0).toUpperCase() + w.slice(1));
/** A school test row ("Monthly test this week.") comes from the year plan's TYPED week tag; 75 of the 176 such tags sat on
 *  another week than the school calendar's (owner ruling A2, 21 Oct 2026), so the card no longer prints it — the week's
 *  "At school" line (src/calendar-events.ts) names the test on the calendar's week. Book tests and unit tests stay. */
const onlySchoolTests = (v: LessonRef["ts"]): boolean => !isNone(v) && v.length > 0 && v.every((x) => x.kind === "school");
function tsHtml(v: LessonRef["ts"], ex: LessonExtras | null): string {
  if (isNone(v)) return noneHtml("ts", v);
  if (onlySchoolTests(v)) return noneHtml("ts", { none: "no-test" });
  return v
    .map((x, row) => {
      if (x.kind === "school") return ""; // (the row index stays the entry's own: the test-audio rows are matched by it)
      if (x.kind === "unit-test") {
        const n = x.page_count ? ` (${x.page_count} pages)` : "";
        return `<span class="bk-main"><b>${esc(x.book)}</b>${esc(n)}</span><span class="bk-pages">Give it when the unit is finished.</span>`;
      }
      const task = taskLabel(x.task);
      const audio = ex?.test_audio?.find((a) => a.row === row && a.book_id === x.book_id);
      const where = [x.test ?? "", x.section ? sectionLabel(x.section) : "", pagesWord(x.pages)].filter(Boolean).join(" · ");
      return `<span class="bk-main">${task ? `<b>${esc(task)}</b> — ` : ""}practise with ${esc(x.book ?? "the exam papers")}</span>${
        where ? `<span class="bk-pages">${esc(where)}</span>`
          : `<span class="bk-pages">${/\.(?:whole|full|half|mini)$/.test(x.task ?? "") ? "Use one whole paper from the book." : "Any test in the book has this part."}</span>`
      }${x.notice ? `<span class="bk-pages">${esc(x.notice)}</span>` : ""}${readerButton(x, x.book, [x.test, task, x.section ? sectionLabel(x.section) : ""].filter(Boolean).join(" · ") || "The test pages", null, "",
        audio ? { tracks: audio.tracks } : {})}${audio ? audio.tracks.map((t) => listenHtml(audio.book_id, t)).join("") : ""}${answerButton(x)}${companionButtons(x.companions)}${x.answer_note ? `<span class="bk-pages">${esc(x.answer_note.split(/(?<=\.)\s/)[0] ?? x.answer_note)}</span>` : ""}`;
    })
    .join("");
}

type RowKey = keyof Omit<LessonRef, "t" | "x"> | "pr";
const ROWS: { key: RowKey; label: string; ico: Parameters<typeof icon>[0]; html: (r: LessonRef, ex: LessonExtras | null) => string; none: (r: LessonRef, ex: LessonExtras | null) => boolean }[] = [
  { key: "cb", label: "Coursebook", ico: "book", html: (r, ex) => cbHtml(r.cb, ex), none: (r) => isNone(r.cb) },
  { key: "wb", label: "Workbook", ico: "pencil", html: (r, ex) => wbHtml(r.wb, ex), none: (r) => isNone(r.wb) },
  { key: "pr", label: "Practice", ico: "sheet", html: (_r, ex) => prHtml(ex), none: (_r, ex) => !ex?.practice?.length },
  { key: "gr", label: "Grammar", ico: "puzzle", html: (r) => grHtml(r.gr), none: (r) => isNone(r.gr) },
  { key: "rd", label: "Reading", ico: "eye", html: (r, ex) => rdHtml(r.rd, ex), none: (r, ex) => isNone(r.rd) && !ex?.reading?.length },
  { key: "ts", label: "Test", ico: "tests", html: (r, ex) => tsHtml(r.ts, ex), none: (r) => isNone(r.ts) || onlySchoolTests(r.ts) },
];

/** The card: the rows in a fixed order (Practice only on weeks that have a worksheet). `compact` = the plan form's narrow column.
 *  `quiet` (teacher review, 15 Oct 2026 — "from not enough resources to many, they are a little overwhelmed"): the rows
 *  with nothing this week ("No workbook pages…", "No grammar…") fold into ONE muted line under the rows that matter, so
 *  the card shows only what a teacher can use. The facts stay one tap away. */
export function booksCardHtml(ref: LessonRef | null, opts: { compact?: boolean; title?: string; quiet?: boolean; bare?: boolean } = {}): string {
  if (!ref) return "";
  const ex = extrasFor(idOf.get(ref));
  const quietRows: { label: string; body: string }[] = [];
  const rows = ROWS.map((r) => {
    if (r.key === "pr" && r.none(ref, ex)) return "";
    const body = r.html(ref, ex);
    const none = r.none(ref, ex);
    if (opts.quiet && none) {
      quietRows.push({ label: r.label, body });
      return "";
    }
    return `<div class="bk-row bk-${r.key}${none ? " is-none" : ""}" data-books-row="${r.key}">
      <dt>${icon(r.ico)}<span>${esc(r.label)}</span></dt>
      <dd>${body || `<span class="bk-none">—</span>`}</dd>
    </div>`;
  }).join("");
  const quiet = quietRows.length
    ? `<details class="bk-quiet"><summary>Nothing this week for: ${esc(quietRows.map((q) => q.label.toLowerCase()).join(", "))}</summary>
        <dl class="books">${quietRows.map((q) => `<div class="bk-row is-none"><dt><span>${esc(q.label)}</span></dt><dd>${q.body || `<span class="bk-none">—</span>`}</dd></div>`).join("")}</dl>
      </details>`
    : "";
  const inner = `<dl class="books">${rows}</dl>${quiet}`;
  if (opts.bare) return `<div class="books-card bare${opts.compact ? " compact" : ""}" data-books-card>${inner}</div>`;
  return `<section class="card books-card${opts.compact ? " compact" : ""}" data-books-card aria-labelledby="books-h">
    <h2 class="card-title" id="books-h">${esc(opts.title ?? "Books for this lesson")}</h2>
    ${inner}
  </section>`;
}

/** The week's Student Book (coursebook) pages as a reader target, or null when the week has no trusted pages.
 *  Used by the lesson page's "Start with these" card and the planning workspace's Book tab. */
export function coursebookTarget(ref: LessonRef | null): { target: ReaderTarget; pagesLabel: string; book: string; unit: string | null } | null {
  if (!ref || isNone(ref.cb) || !ref.cb.book_id || !ref.cb.pdf?.length) return null;
  const cb = ref.cb;
  const ex = extrasFor(idOf.get(ref));
  const what = cb.unit ?? (cb.parts.map((p) => p[0]).filter(Boolean).join(", ") || "This week's pages");
  const target: ReaderTarget = { book: cb.book, bookId: cb.book_id!, label: what, pages: cb.pdf! };
  if (cb.unit_pdf?.length) target.unit = { label: cb.unit ? `${cb.unit} · the whole unit` : what, pages: cb.unit_pdf };
  if (ex?.cb_tracks?.length) target.tracks = ex.cb_tracks;
  if (cb.answers) target.guide = cb.answers;
  else if (ex?.cb_answers) target.guide = ex.cb_answers;
  const pages = cb.parts.map((p) => p[1]).filter(Boolean) as string[];
  return { target, pagesLabel: pages.length ? pagesWord(joinPages(pages)) : "", book: cb.book, unit: cb.unit };
}

/** The week's workbook pages as a reader target (null when none are trusted). */
export function workbookTarget(ref: LessonRef | null): { target: ReaderTarget; pagesLabel: string; book: string } | null {
  if (!ref || isNone(ref.wb) || !ref.wb.book_id || !ref.wb.pdf?.length) return null;
  const wb = ref.wb;
  const ex = extrasFor(idOf.get(ref));
  const target: ReaderTarget = { book: wb.book, bookId: wb.book_id!, label: "This week's workbook pages", pages: wb.pdf! };
  if (wb.answers) target.guide = wb.answers;
  else if (ex?.wb_answers) target.guide = ex.wb_answers;
  const pages = wb.parts.map((p) => p[1]).filter(Boolean) as string[];
  return { target, pagesLabel: pages.length ? pagesWord(joinPages(pages)) : "", book: wb.book };
}

/** One short line for Home's class card: the Student Book pages and the workbook pages. */
export function booksLineHtml(ref: LessonRef | null): string {
  if (!ref) return "";
  const bits: string[] = [];
  if (!isNone(ref.cb)) {
    const pages = ref.cb.parts.map((p) => p[1]).filter(Boolean) as string[];
    bits.push(`<span class="lcb">${icon("book", "ico-sm")}<span>${esc(ref.cb.book.replace(/ Student Book$/, ""))}${ref.cb.unit ? ` · ${esc(ref.cb.unit.replace(/:.*$/, ""))}` : ""}${pages.length ? ` · ${esc(pagesWord(joinPages(pages)))}` : ""}</span></span>`);
  }
  if (!isNone(ref.wb)) {
    const pages = ref.wb.parts.map((p) => p[1]).filter(Boolean) as string[];
    if (pages.length) bits.push(`<span class="lcb">${icon("pencil", "ico-sm")}<span>Workbook ${esc(pagesWord(joinPages(pages)))}</span></span>`);
  }
  if (!isNone(ref.gr)) bits.push(`<span class="lcb">${icon("puzzle", "ico-sm")}<span>Grammar for Schools Unit ${esc(String(ref.gr.unit))}</span></span>`);
  return bits.length ? `<p class="lc-books" data-books-line>${bits.join("")}</p>` : "";
}

/** "4–5" + "6–7" → "4–7" when they run on; otherwise "4–5, 8–9" (spans sorted first, so an earlier
 *  range is never swallowed by a later one: "14–15" + "6–18" → "6–18"). */
function joinPages(list: string[]): string {
  const spans = list.map((p) => p.split(/[–-]/).map(Number)).filter((a) => a.every(Number.isFinite)) as number[][];
  if (spans.length !== list.length || !spans.length) return list.join(", ");
  spans.sort((a, b) => a[0]! - b[0]!);
  const merged: number[][] = [];
  for (const s of spans) {
    const a = s[0]!, b = s[s.length - 1]!;
    const last = merged[merged.length - 1];
    if (last && a <= last[1]! + 1) last[1] = Math.max(last[1]!, b);
    else merged.push([a, b]);
  }
  return merged.map(([a, b]) => (a === b ? `${a}` : `${a}–${b}`)).join(", ");
}

/** The books as plain lines, for the plan form's "Add these books to Materials" (the teacher's own
 *  list box, which already accepts paste — the thinking and procedure boxes are never touched). */
export function booksPlainLines(ref: LessonRef | null): string[] {
  if (!ref) return [];
  const out: string[] = [];
  if (!isNone(ref.cb)) {
    const parts = ref.cb.parts.map(([l, p]) => `${l} ${pagesWord(p)}`.trim()).filter(Boolean).join("; ");
    out.push(`${ref.cb.book}${ref.cb.unit ? `, ${ref.cb.unit}` : ""}${parts ? `: ${parts}` : ref.cb.note ? ` (${ref.cb.note.replace(/\.$/, "")})` : ""}`);
  }
  if (!isNone(ref.wb)) out.push(`${ref.wb.book}: ${ref.wb.parts.map(([l, p]) => `${l} ${pagesWord(p)}`.trim()).join("; ")}`);
  if (!isNone(ref.gr)) out.push(`${ref.gr.book}, Unit ${ref.gr.unit}: ${ref.gr.title} (${pagesWord(ref.gr.pages)}${ref.gr.hw ? ", homework" : ""})`);
  if (!isNone(ref.rd)) for (const r of ref.rd) out.push(`${r.book}, ${r.part}${r.pages ? ` (${pagesWord(r.pages)})` : ""}`);
  return out;
}
